import type { FC } from 'react';
import IconMdi from '@components/IconMdi';
import Tooltip from '@components/Tooltip';
import { PanelPlacement } from '@ContractBuilder/hooks';
import { mdiCompareHorizontal, mdiDockLeft, mdiDockRight, mdiSwapHorizontal } from '@mdi/js';
import clsx from 'clsx';

import { selectCanEnableSplit, selectIsInSplitMode, usePDFPreviewStore } from '../store/pdf-preview.store';
import { getPlacementFlags } from '../utils/getPlacementFlags';

import { buttonDisabledClasses, placementButtonClasses, splitDocumentsButtonClasses } from './classes';

interface PlacementControlsProps {
  className?: string;
}

export const PlacementControls: FC<PlacementControlsProps> = ({ className }) => {
  const { placement, setPlacement, toggleSplitMode, isInSplitMode, canEnableSplit } = usePDFPreviewStore((state) => ({
    placement: state.placement,
    setPlacement: state.setPlacement,
    toggleSplitMode: state.toggleSplitMode,
    isInSplitMode: selectIsInSplitMode(state),
    canEnableSplit: selectCanEnableSplit(state),
  }));

  const { isLeftSidePlacement, isRightSidePlacement } = getPlacementFlags(placement, isInSplitMode);

  return (
    <div
      className={clsx(
        'mr-3 flex shrink-0 flex-row overflow-hidden rounded-md border border-gray-200 bg-white',
        className,
      )}
    >
      <Tooltip content="Toggle to left side">
        <button
          className={clsx(placementButtonClasses, (isLeftSidePlacement || isInSplitMode) && buttonDisabledClasses)}
          onClick={() => setPlacement(PanelPlacement.Left)}
          title="Change PDF sidebar preview placement"
        >
          <IconMdi className="transition-colors hover:text-gray-400" path={mdiDockLeft} />
        </button>
      </Tooltip>

      {canEnableSplit && (
        <Tooltip content={isInSplitMode ? 'Close compare view' : 'Compare view'}>
          <button
            className={splitDocumentsButtonClasses({ isInSplitMode })}
            onClick={toggleSplitMode}
            title="Split documents"
          >
            <IconMdi
              className="transition-colors hover:text-gray-400"
              path={isInSplitMode ? mdiCompareHorizontal : mdiSwapHorizontal}
            />
          </button>
        </Tooltip>
      )}

      <Tooltip content="Toggle to right side">
        <button
          className={clsx(
            placementButtonClasses,
            'border-r-0',
            (isRightSidePlacement || isInSplitMode) && buttonDisabledClasses,
          )}
          onClick={() => setPlacement(PanelPlacement.Right)}
          title="Change PDF sidebar preview placement"
        >
          <IconMdi className="transition-colors hover:text-gray-400" path={mdiDockRight} />
        </button>
      </Tooltip>
    </div>
  );
};
